import dayjs from 'dayjs'
import { ParsedAnswer } from '../../Teleexpertise/questionTypes/_props'
import { PatientInfoForFormula, WorkflowFormQuestion } from './types'
import { getFirstUnansweredQuestionId, getWorkflowPath } from './navigator'

/**
 * Merges request and local answers into a single array.
 * Request answers are set first, then overridden by local values if present.
 * Returns a flat array with auto: false for each entry.
 */
export const mergeAnswers = (
    request?: Array<{ id: string; value: any; auto: boolean }>,
    local?: Map<string, any[]>
): Array<{ id: string; value: any; auto: boolean }> => {
    const merged = new Map<string, any>()

    for (const answer of request ?? []) {
        if (!answer?.id) continue
        merged.set(answer.id, Array.isArray(answer.value) ? answer.value : answer.value)
    }
    if (local) {
        for (const [qid, vals] of local.entries()) {
            if (!qid) continue
            if (!Array.isArray(vals) || vals.length === 0) continue
            merged.set(qid, vals)
        }
    }
    return Array.from(merged.entries()).map(([id, value]) => ({
        id,
        value,
        auto: false,
    }))
}

/**
 * Find the first unanswered question using trigger-based graph traversal.
 * This respects the workflow conditional branches instead of linear indexing.
 */
export const getFirstUnansweredQuestion = (
    questions: WorkflowFormQuestion[],
    request?: Array<{ id: string; value: any; auto: boolean }>,
    local?: Map<string, any[]>,
    rawWorkflowContent?: any[] | null
): WorkflowFormQuestion | null => {
    if (questions.length === 0) return null

    const effectiveMap = new Map<string, any[]>()
    if (request) {
        for (const r of request) {
            if (r.id) {
                effectiveMap.set(r.id, Array.isArray(r.value) ? r.value : [r.value])
            }
        }
    }
    if (local) {
        for (const [qid, vals] of local.entries()) {
            if (qid) {
                effectiveMap.set(qid, vals)
            }
        }
    }
    const firstUnansweredId = getFirstUnansweredQuestionId(questions, effectiveMap, rawWorkflowContent ?? null)
    if (firstUnansweredId) {
        return questions.find(q => q.id === firstUnansweredId) ?? null
    }
    const effective = mergeAnswers(request, local)
    const answeredIds = new Set(effective?.map(r => r.id) || [])
    return questions.find(q => q.id && !answeredIds.has(q.id)) ?? questions[questions.length - 1]
}

/**
 * Get workflow progress using trigger-based graph traversal.
 * This ensures the progress bar reflects the actual path the user is on.
 *
 * totalSteps uses the count of non-auto questions in the full workflow (not just the path)
 * so that the progress bar shows the full scope from the start.
 * completedSteps and currentStep use the trigger-based path for accuracy.
 */
export const getWorkflowProgress = (
    questions: WorkflowFormQuestion[],
    requestAnswers: Array<{ id: string; value: any; auto: boolean }>,
    committedAnswers: Map<string, any[]>,
    localAnswers: Map<string, any[]>,
    currentQuestionId?: string | null
) => {
    if (questions.length === 0) {
        return { currentStep: 0, totalSteps: 0, completedSteps: 0, isComplete: false }
    }
    const effectiveMap = new Map<string, any[]>()

    if (requestAnswers) {
        for (const request of requestAnswers) {
            if (request.id) {
                effectiveMap.set(request.id, Array.isArray(request.value) ? request.value : [request.value])
            }
        }
    }
    if (committedAnswers) {
        for (const [questionId, vals] of committedAnswers.entries()) {
            if (questionId && vals && vals.length > 0) {
                effectiveMap.set(questionId, vals)
            }
        }
    }
    if (localAnswers) {
        for (const [questionId, vals] of localAnswers.entries()) {
            if (questionId && vals && vals.length > 0) {
                effectiveMap.set(questionId, vals)
            }
        }
    }
    const nonAutoQuestions = questions.filter(question => !question.auto)
    const totalSteps = nonAutoQuestions.length
    if (totalSteps === 0) {
        return { currentStep: 0, totalSteps: 0, completedSteps: 0, isComplete: true }
    }
    const completedSteps = nonAutoQuestions.filter(question => {
        const answer = effectiveMap.get(question.id)
        return answer && answer.length > 0
    }).length
    let currentStep = completedSteps

    if (currentQuestionId) {
        const idx = nonAutoQuestions.findIndex(question => question.id === currentQuestionId)
        if (idx !== -1) {
            const currentAnswer = effectiveMap.get(currentQuestionId)
            const isCurrentAnswered = currentAnswer && currentAnswer.length > 0
            if (isCurrentAnswered) {
                currentStep = idx + 1
            } else {
                currentStep = idx
            }
        }
    }
    currentStep = Math.max(0, Math.min(currentStep, totalSteps))
    const isComplete = completedSteps >= totalSteps && currentStep >= totalSteps
    return {
        totalSteps,
        completedSteps,
        currentStep,
        isComplete,
    }
}

const buildQidToAnswerIdMap = (questions: WorkflowFormQuestion[]): Map<string, string> => {
    const map = new Map<string, string>()
    for (const question of questions) {
        if (question.id && question.answerId) map.set(question.id, question.answerId)
    }
    return map
}

/**
 * Builds a flat key-value object of all saved answers for formula evaluation.
 *
 * This function aggregates answers from multiple sources (request, committed, local)
 * and maps them to both question IDs and their corresponding answer IDs.
 * It also enriches the data with patient information (sex, age) when available.
 *
 * The resulting object is used by the formula engine to compute dynamic values
 * (e.g., risk scores, conditional logic, automatic calculations).
 *
 * @param workflowQuestions - The full list of workflow questions
 * @param requestAnswers - Answers from the current HTTP request
 * @param committedAnswersByQuestionId - Persisted answers from the database
 * @param localAnswersByQuestionId - Temporary/local answers not yet committed
 * @param excludeQuestionId - Question ID to exclude (prevents self-referential loops)
 * @param patient - Optional patient data (sex, birthDate) for formula context
 * @returns A flat record mapping question/answer IDs to their values
 */
export const buildSavedValuesForFormula = (
    workflowQuestions: WorkflowFormQuestion[],
    requestAnswers: ParsedAnswer[],
    committedAnswersByQuestionId: Map<string, any[]>,
    localAnswersByQuestionId: Map<string, any[]>,
    excludeQuestionId: string,
    patient?: PatientInfoForFormula | null
): Record<string, any> => {
    const qidToAnswerId = buildQidToAnswerIdMap(workflowQuestions)
    const savedValues: Record<string, any> = {}

    const setSavedValue = (key: string, value: any) => {
        savedValues[key] = value
        const answerId = qidToAnswerId.get(key)
        if (answerId && answerId !== key) savedValues[answerId] = value
    }
    for (const answer of requestAnswers ?? []) {
        if (answer.id) setSavedValue(answer.id, answer.value)
    }
    for (const [id, vals] of committedAnswersByQuestionId) {
        if (vals?.length) setSavedValue(id, vals[0])
    }
    for (const [id, vals] of localAnswersByQuestionId) {
        if (id !== excludeQuestionId && vals?.length) setSavedValue(id, vals[0])
    }
    if (patient) {
        if (patient.sex) {
            const sexMapping: Record<string, string> = {
                'M': 'M',
                'F': 'F',
                '1': 'M',
                '2': 'F',
            }
            const normalizedSex = patient.sex.toUpperCase()
            const sexCode = sexMapping[normalizedSex] ?? normalizedSex
            setSavedValue('S.A.1.4', sexCode)
        }
        if (patient.birthDate) {
            try {
                const age = dayjs().diff(dayjs(patient.birthDate), 'year')
                setSavedValue('S.A.1.7', age)
            } catch {}
        }
    }
    return savedValues
}

